import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Satellite,
  Cpu,
  GraduationCap,
  Layers,
  Wallet,
  Wrench,
  MapPin,
  Database,
  Radar,
  Compass,
} from "lucide-react";
import { Reveal, MaskLines, Eyebrow, SectionHead, Counter, HUDFrame, EASE } from "@/components/anim";
import Marquee from "@/components/Marquee";
import { IMAGES, STATS, PILLARS, CHALLENGES, EDGE, FLYWHEEL, LOOP, ROADMAP } from "@/lib/content";

const PILLAR_ICONS = [Cpu, Satellite, GraduationCap];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const rotateX = useSpring(rotX, { stiffness: 120, damping: 16 });
  const rotateY = useSpring(rotY, { stiffness: 120, damping: 16 });

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden hud-grid noise">
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-kinetic/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[480px] w-[480px] rounded-full bg-ice/8 blur-[120px]" />

      <div className="pointer-events-none absolute right-[8%] top-[18%] hidden lg:block opacity-40">
        <div className="relative h-56 w-56 rounded-full border border-kinetic/25">
          <div className="absolute inset-3 rounded-full border border-kinetic/20" />
          <div className="absolute inset-8 rounded-full border border-kinetic/15" />
          <div
            className="absolute inset-0 rounded-full animate-radar"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(255,122,26,0.35), rgba(255,122,26,0.02) 70deg, transparent 70deg)",
            }}
          />
          <Radar className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-kinetic" />
        </div>
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 md:px-8 pt-32 md:pt-40 pb-16 lg:grid-cols-[1.15fr_0.85fr] lg:items-center min-h-screen">
        <motion.div style={{ y: textY }}>
          <MaskLines
            lines={[
              <span key="e" className="font-hud text-[11px] md:text-xs uppercase tracking-[0.3em] text-kinetic flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-kinetic animate-blink" />
                Aeris India Technology — India's integrated drone company
              </span>,
            ]}
            delay={0.15}
          />
          <MaskLines
            className="mt-7 font-display font-extrabold tracking-[-0.02em] leading-[0.95] text-[clamp(2.9rem,7.4vw,6.6rem)]"
            lines={["ENGINEERED", "IN INDIA.", <span key="s">BUILT TO <span className="text-kinetic">SCALE.</span></span>]}
            delay={0.3}
            stagger={0.16}
          />
          <MaskLines
            className="mt-8 max-w-xl text-base md:text-lg text-[#9CA3AF] leading-relaxed"
            lines={[
              <span key="p" className="block">
                We design, build and deploy intelligent drones that power India's progress —
                manufacturing, Drone-as-a-Service and pilot training under one roof.
              </span>,
            ]}
            delay={0.85}
          />
          <MaskLines
            className="mt-10 flex flex-wrap items-center gap-4"
            lines={[
              <span key="c" className="flex flex-wrap items-center gap-4">
                <Link
                  to="/services"
                  data-testid="hero-cta-book-flight"
                  className="group inline-flex items-center gap-3 rounded-full bg-kinetic px-7 py-4 font-hud text-xs font-semibold uppercase tracking-[0.18em] text-void transition-all duration-300 hover:shadow-[0_0_44px_rgba(255,122,26,0.5)] hover:-translate-y-0.5"
                >
                  Book a Flight
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
                <Link
                  to="/solutions"
                  data-testid="hero-cta-explore-solutions"
                  className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 font-hud text-xs font-semibold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:border-kinetic hover:text-kinetic"
                >
                  Explore Solutions
                </Link>
              </span>,
            ]}
            delay={1.05}
          />
        </motion.div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
          className="relative"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            rotY.set(((e.clientX - r.left) / r.width - 0.5) * 7);
            rotX.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
          }}
          onMouseLeave={() => {
            rotX.set(0);
            rotY.set(0);
          }}
        >
          <motion.div style={{ rotateX, rotateY, transformPerspective: 1000 }} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
            <HUDFrame testid="hero-image-frame" className="relative">
              <div className="overflow-hidden">
                <img
                  src={IMAGES.heroHangar}
                  alt="Aeris drone in launch hangar"
                  className="h-[420px] w-full object-cover img-treat md:h-[520px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-void/20" />
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap justify-between gap-2 font-hud text-[10px] tracking-[0.2em] text-kinetic/90">
                <span>SYS NOMINAL</span>
                <span>RTK FIXED</span>
                <span>ALT 120M AGL</span>
                <span className="hidden sm:inline">LINK 98%</span>
              </div>
            </HUDFrame>
            <div className="absolute -top-3 -right-3 animate-floaty rounded-full border border-kinetic/40 bg-void/80 px-4 py-2 font-hud text-[10px] tracking-[0.2em] text-kinetic backdrop-blur">
              FLEET READY
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-white/10 bg-surface">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <div key={s.testid} className="bg-surface px-6 py-10 md:px-10 md:py-14" data-testid={s.testid}>
            <Reveal delay={i * 0.08}>
              <Counter value={s.value} suffix={s.suffix} className="font-display text-3xl md:text-5xl font-extrabold text-kinetic tracking-tight" />
              <p className="mt-4 text-sm text-[#F9FAFB] leading-snug">{s.label}</p>
              <p className="mt-2 font-hud text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{s.source}</p>
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhoWeAre() {
  return (
    <section className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHead
            sec="01 / WHO WE ARE"
            eyebrow="Aeris India Technology"
            title={
              <>
                We design, build and deploy intelligent drones that power{" "}
                <span className="text-kinetic">India's progress.</span>
              </>
            }
          />
          <Reveal delay={0.2} className="mt-8 space-y-4">
            {[
              "End-to-end ecosystem from hardware to insight",
              "Design, develop, manufacture and deploy drones",
              "Serving agriculture, defence, infrastructure, mining, disaster management and enterprise",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 border-b border-white/8 pb-4">
                <span className="font-hud text-[11px] text-kinetic mt-1">0{i + 1}</span>
                <p className="text-[#E5E7EB]">{item}</p>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.3} className="mt-10 rounded-xl border border-kinetic/25 bg-panel/60 p-7 glow-cyan">
            <p className="font-display text-xl md:text-2xl font-semibold leading-snug">
              "Integration is our advantage. Aeris captures value before, during and after every flight."
            </p>
            <p className="mt-4 text-sm text-[#9CA3AF] leading-relaxed">
              Manufacturing lowers fleet cost. Services create recurring utilisation. Training expands
              capacity. Field data improves product design — a compounding loop single-offering
              competitors cannot match.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <HUDFrame testid="whoweare-image-frame">
            <div className="overflow-hidden">
              <img src={IMAGES.darkCloseup} alt="Aeris drone hardware" className="h-[440px] md:h-[560px] w-full object-cover img-treat" />
              <div className="absolute inset-0 bg-gradient-to-tr from-void/60 via-transparent to-kinetic/10" />
            </div>
          </HUDFrame>
        </Reveal>
      </div>
    </section>
  );
}

function Challenge() {
  return (
    <section className="border-y border-white/10 bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead
          sec="02 / THE CHALLENGE"
          eyebrow="Why drones stay grounded"
          title={
            <>
              Critical operations. Structural barriers. <span className="text-outline-cyan">Slower progress.</span>
            </>
          }
        />
        <div className="mt-14">
          {CHALLENGES.map((c, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="group flex items-center gap-6 border-t border-white/10 py-7 transition-all duration-300 hover:border-kinetic/40 hover:pl-4" data-testid={`challenge-item-${i + 1}`}>
                <span className="font-hud text-sm text-kinetic/70">0{i + 1}</span>
                <p className="font-display text-xl md:text-3xl font-semibold tracking-tight text-[#D1D5DB] group-hover:text-white transition-colors">
                  {c}
                </p>
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-kinetic opacity-0 transition-all duration-300 group-hover:opacity-100" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead
          sec="03 / THE AERIS SOLUTION"
          eyebrow="Three pillars. One ecosystem."
          title="We design, build and deploy drones — then deliver the data and trained people behind them."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <Reveal key={p.index} delay={i * 0.1} className="h-full">
                <div
                  className="group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-panel/50 p-8 transition-all duration-500 hover:border-kinetic/50 hover:shadow-[0_0_40px_rgba(255,122,26,0.12)]"
                  data-testid={`pillar-card-${p.name.toLowerCase()}`}
                >
                  <span className="font-display text-6xl font-extrabold text-white/8 absolute right-6 top-4">{p.index}</span>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-kinetic/30 bg-kinetic/10 text-kinetic">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-16 font-display text-2xl font-bold tracking-tight">{p.name}</h3>
                  <p className="mt-3 text-[#9CA3AF] leading-relaxed">{p.desc}</p>
                  <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-kinetic transition-all duration-500 group-hover:w-full" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Edge() {
  const icons = [Layers, Wallet, MapPin, Database, Compass];
  return (
    <section className="border-y border-white/10 bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead sec="04 / OUR EDGE" eyebrow="Unfair advantages that set Aeris apart" title="Built different. On purpose." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-6">
          {EDGE.map((e, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={i} delay={i * 0.07} className={`${i < 3 ? "lg:col-span-2" : "lg:col-span-3"} h-full`}>
                <div className="h-full rounded-2xl border border-white/10 bg-panel/50 p-7 transition-all duration-300 hover:border-kinetic/40" data-testid={`edge-card-${i + 1}`}>
                  <Icon className="h-6 w-6 text-kinetic" />
                  <h3 className="mt-6 font-display text-lg font-bold leading-snug">{e.title}</h3>
                  <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{e.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Flywheel() {
  return (
    <section className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead
          sec="05 / BUSINESS MODEL"
          eyebrow="Revenue that reinforces itself"
          title={
            <>
              Four revenue streams. <span className="text-kinetic">One powerful flywheel.</span>
            </>
          }
        />
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {FLYWHEEL.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.07} className="h-full">
                <div className="h-full rounded-2xl border border-white/10 bg-panel/50 p-6" data-testid={`flywheel-stream-${f.name.toLowerCase().replace(/-/g, "")}`}>
                  <p className="font-hud text-[11px] tracking-[0.22em] text-kinetic">STREAM 0{i + 1}</p>
                  <h3 className="mt-4 font-display text-xl font-bold">{f.name}</h3>
                  <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="h-full">
            <div className="h-full rounded-2xl border border-kinetic/25 bg-gradient-to-b from-panel/80 to-surface p-8 glow-cyan">
              <p className="font-hud text-[11px] uppercase tracking-[0.28em] text-kinetic">The compounding loop</p>
              <div className="mt-8 space-y-0">
                {LOOP.map((l, i) => (
                  <div key={i} className="relative flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 border-l border-kinetic/30 pb-8 pl-6 last:pb-0" data-testid={`flywheel-loop-step-${i + 1}`}>
                    <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-kinetic" />
                    <p className="font-display text-base md:text-lg font-semibold flex-1">{l.act}</p>
                    <p className="font-hud text-xs tracking-[0.14em] text-kinetic sm:text-right">{l.out}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  return (
    <section className="border-t border-white/10 bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead
          sec="06 / VISION AND ROADMAP"
          eyebrow="A phased path"
          title="Prove demand. Deepen capability. Compound network effects."
        />
        <div className="mt-16 grid gap-10 md:grid-cols-4">
          {ROADMAP.map((r, i) => (
            <Reveal key={r.phase} delay={i * 0.1}>
              <div className="relative" data-testid={`roadmap-phase-${r.phase}`}>
                <div className="flex items-center gap-4">
                  <span className="font-display text-4xl font-extrabold text-kinetic">{r.phase}</span>
                  {i < 3 && <span className="hidden md:block h-px flex-1 bg-gradient-to-r from-kinetic/50 to-transparent" />}
                </div>
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{r.name}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <StatsBand />
      <WhoWeAre />
      <Challenge />
      <Pillars />
      <Marquee />
      <Edge />
      <Flywheel />
      <Roadmap />
    </main>
  );
}
